import type {
  RaDefaultLibraryView,
  RaDefaultPullResult,
  RaFindingDetail,
  RaFindingListView,
  RaFindingSource,
  RaImportResult,
  RaLibraryDetail,
  RaLibraryList,
  RaLibrarySource,
  RaMasterItem,
  RaMasterSource,
  RaMasterView,
  RaRegisterSource,
  RaRegisterView,
  RaReviewTask,
  RaVerificationDetail,
  RaVerificationListView,
  RaVerificationSource,
  RaVersionView,
  RiskDetail,
  RiskSource,
  RiskSummary,
} from '@felix/ui';
import {
  browserPdfOutput,
  type RaDashboardData,
  type RaDashboardSource,
  type RaReportsData,
  type RaSettings,
  type RaSettingsSource,
  type ReportBrand,
} from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';

/**
 * Ofiste risk değerlendirmesi (M13d; ADR 0050): gemilerin değerlendirmeleri ve ofis adımının
 * kararı; şirket RA kütüphanesi Risk ▸ Library'de yönetilir (ADR 0087). Değerlendirme yazma yok
 * (gemi verisi; mimari belge §8).
 */
export const officeRiskSource: RiskSource = {
  scope: 'web',
  vessels: async () => (await officeApi.vessels()).map((v) => ({ id: v.id, name: v.name })),
  list: () => api<RiskSummary[]>('/office/risk-assessments'),
  detail: (id) => api<RiskDetail>(`/office/risk-assessments/${id}`),
  // Şirket RA kütüphanesi (ADR 0087): yayımlanmış değerlendirmeler, burada salt okunur
  library: async () =>
    (await api<RaLibraryList>('/office/risk/library')).items
      .filter((i) => i.publishedRevision !== null)
      .map((i) => ({
        id: i.id,
        code: i.code,
        revision: i.publishedRevision ?? i.revision,
        title: i.title,
        scope: null,
        inherent: i.inherent,
        current: i.assignments.length > 0,
        reason: i.assignments.length > 0 ? null : ('not_assigned' as const),
      })),
  decide: (id, input) =>
    api<RiskDetail>(`/office/risk-assessments/${id}/decision`, { method: 'POST', body: input }),
  // Şirketin risk matrisi profilleri (ADR 0085): matris, lejant ve taslakların seviyesi
  matrix: () => officeApi.riskMatrix(),
};

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Ham `.xlsx` gövdesiyle içe aktarma (ADR 0086 §5): en çok 10 MB, kalıcı depoya yazılmaz. */
async function importLibrary(file: Blob, preview: boolean): Promise<RaImportResult> {
  let response: Response;
  try {
    response = await fetch(`/api/office/risk/master/import?preview=${preview}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': XLSX },
      body: file,
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string; detail?: unknown };
  if (!response.ok) {
    throw new ApiError(
      data.error ?? 'INTERNAL',
      response.status,
      typeof data.detail === 'string' ? data.detail : undefined,
      data,
    );
  }
  return data as RaImportResult;
}

/**
 * Risk ▸ Master Data (K2; ADR 0086): şirketin risk ana verisi; ofis yazar ve Excel'den içe
 * aktarır, gemiye iner.
 */
export const officeRaMasterSource: RaMasterSource = {
  scope: 'web',
  load: () => api<RaMasterView>('/office/risk/master'),
  editor: {
    save: (kind, id, rowVersion, content) =>
      id === null
        ? api<RaMasterItem<typeof kind>>(`/office/risk/master/${kind}`, {
            method: 'POST',
            body: { content },
          })
        : api<RaMasterItem<typeof kind>>(`/office/risk/master/${kind}/${id}`, {
            method: 'PATCH',
            body: { rowVersion, content },
          }),
    setStatus: async (kind, items, status) =>
      (
        await api<{ changed: number }>(`/office/risk/master/${kind}/status`, {
          method: 'POST',
          body: { items, status },
        })
      ).changed,
    importFile: importLibrary,
    // Ürünün varsayılan RA kütüphanesi (ADR 0106)
    defaultLibrary: {
      load: () => api<RaDefaultLibraryView>('/office/risk/master/default-library'),
      pull: (codes, preview) =>
        api<RaDefaultPullResult>(`/office/risk/master/default-library/pull?preview=${preview}`, {
          method: 'POST',
          body: { codes },
        }),
    },
    reviewActivity: async (id, rowVersion, input) => {
      await api<{ ok: true }>(`/office/risk/master/activities/${id}/review`, {
        method: 'POST',
        body: { rowVersion, ...input },
      });
    },
    decideTask: (id, rowVersion, input) =>
      api<RaReviewTask>(`/office/risk/master/review-tasks/${id}/decision`, {
        method: 'POST',
        body: { rowVersion, ...input },
      }),
  },
};

const LIBRARY = '/office/risk/library';

/**
 * Risk ▸ Library (K3; ADR 0087): şirket RA'sı; Builder, iş akışı, yayım ve atama ofiste. Parola
 * yalnız şirket ayarı açıkken (ADR 0075).
 */
export const officeRaLibrarySource: RaLibrarySource = {
  scope: 'web',
  version: (id) => api<RaVersionView>(`${LIBRARY}/versions/${id}`),
  office: {
    list: () => api<RaLibraryList>(LIBRARY),
    detail: (id) => api<RaLibraryDetail>(`${LIBRARY}/${id}`),
    create: (activityId) => api<RaLibraryDetail>(LIBRARY, { method: 'POST', body: { activityId } }),
    drafts: () =>
      api<{ created: string[]; skipped: string[] }>(`${LIBRARY}/drafts`, {
        method: 'POST',
        body: {},
      }),
    save: (id, rowVersion, content) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}`, { method: 'PATCH', body: { rowVersion, content } }),
    event: (id, input) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}/events`, { method: 'POST', body: input }),
    publish: (id, contentHash, password) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}/publish`, {
        method: 'POST',
        body: { contentHash, ...(password ? { password } : {}) },
      }),
    revise: (id, rowVersion) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}/revision`, { method: 'POST', body: { rowVersion } }),
    assign: (id, target) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}/assignments`, { method: 'POST', body: target }),
    unassign: (id, assignmentId, rowVersion) =>
      api<RaLibraryDetail>(`${LIBRARY}/${id}/assignments/${assignmentId}/remove`, {
        method: 'POST',
        body: { rowVersion },
      }),
  },
};

/**
 * Risk ▸ Vessel Risk Register (K4; ADR 0088): seçilen geminin güncel sürümleri ve gemiden gelen
 * okundu onayları; ofiste salt okunur (onay gemi verisidir).
 */
export const officeRaRegisterSource: RaRegisterSource = {
  scope: 'web',
  // Sürüm PDF'i (ADR 0095 §2): onay izi ofiste sürümle gelir
  brand: officeBrand,
  pdf: browserPdfOutput(),
  load: (vesselId) =>
    api<RaRegisterView>(
      `/office/risk/register${vesselId ? `?vessel=${encodeURIComponent(vesselId)}` : ''}`,
    ),
  version: (id) => api<RaVersionView>(`${LIBRARY}/versions/${id}`),
};

/**
 * Risk ▸ Operational Verification (K5; ADR 0089): seçilen geminin operasyonel doğrulamaları; ofiste
 * salt okunur (doğrulama gemi verisidir).
 */
export const officeRaVerificationSource: RaVerificationSource = {
  scope: 'web',
  // Doğrulama kaydının PDF'i (ADR 0095 §2)
  brand: officeBrand,
  pdf: browserPdfOutput(),
  list: (vesselId) =>
    api<RaVerificationListView>(
      `/office/risk/verifications${vesselId ? `?vessel=${encodeURIComponent(vesselId)}` : ''}`,
    ),
  detail: (id) => api<RaVerificationDetail>(`/office/risk/verifications/${id}`),
};

/** PDF başlığındaki şirket markası (ADR 0057 §7); okunamazsa yok. */
async function officeBrand(): Promise<ReportBrand | null> {
  try {
    const { branding } = await officeApi.branding();
    return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
  } catch {
    return null;
  }
}

const query = (values: Record<string, string | null | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) if (value) params.set(key, value);
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * Risk ▸ Dashboard ve Monitoring & Reports (K7; ADR 0095): filo ya da seçilen gemi; gemi verisi
 * ofiste salt okunur.
 */
export const officeRaDashboardSource: RaDashboardSource = {
  scope: 'web',
  load: (vesselId) => api<RaDashboardData>(`/office/risk/dashboard${query({ vessel: vesselId })}`),
  reports: ({ vesselId, from, to }) =>
    api<RaReportsData>(`/office/risk/reports${query({ vessel: vesselId, from, to })}`),
};

type RaSettingsResult = { settings: RaSettings; can: { edit: boolean } };

/** Settings ▸ Risk Assessment (K7; ADR 0095 §3): "Ayarlar / düzenleme" yetkisiyle yazılır. */
export const officeRaSettingsSource: RaSettingsSource = {
  scope: 'web',
  load: () => api<RaSettingsResult>('/office/risk/settings'),
  save: (rowVersion, values) =>
    api<RaSettingsResult>('/office/risk/settings', {
      method: 'PATCH',
      body: { rowVersion, ...values },
    }),
};

/**
 * Risk ▸ Findings & Actions (K6; ADR 0094): seçilen geminin bulguları salt okunur; ofisin kararı
 * (DPA'nın yükseltme kararı, gözden geçirenin değişiklik talebi kararı).
 */
export const officeRaFindingSource: RaFindingSource = {
  scope: 'web',
  list: (vesselId) =>
    api<RaFindingListView>(
      `/office/risk/findings${vesselId ? `?vessel=${encodeURIComponent(vesselId)}` : ''}`,
    ),
  detail: (id) => api<RaFindingDetail>(`/office/risk/findings/${id}`),
  // Kanıt dosyaları salt okunur; gemiden gelmişse açılır (ADR 0121 §1)
  attachments: (id) => officeApi.pmsAttachments(id, 'ra_finding'),
  attachmentUrl: (id) => `/api/office/attachments/${encodeURIComponent(id)}`,
  decide: (id, input) =>
    api<RaFindingDetail>(`/office/risk/findings/${id}/decisions`, {
      method: 'POST',
      body: input,
    }),
};
